import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";

function Compare() {
  const [allArtists, setAllArtists] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [hoveredAlbums, setHoveredAlbums] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pop, rap] = await Promise.all([
          axios.get("https://critica-backend.onrender.com/genre/pop"),
          axios.get("https://critica-backend.onrender.com/genre/rap"),
        ]);
        const all = [...pop.data, ...rap.data];
        const cleaned = all.map((artist) => ({
          ...artist,
          albums: artist.albums
            .map((a) => ({
              ...a,
              album: a.album || "Unknown Album",
              release_year: isNaN(parseInt(a.release_year)) ? null : parseInt(a.release_year),
              pitchfork: isNaN(parseFloat(a.pitchfork)) ? null : parseFloat(a.pitchfork),
              metacritic: isNaN(parseFloat(a.metacritic)) ? null : parseFloat(a.metacritic),
              fantano: isNaN(parseFloat(a.fantano)) ? null : parseFloat(a.fantano),
            }))
            .filter((a) => a.release_year !== null),
        }));
        setAllArtists(cleaned);
      } catch (err) {
        console.error("Failed to fetch artists", err);
      }
    };
    fetchData();
  }, []);

  const handleSelect = (e) => {
    const name = e.target.value;
    if (name && !selectedArtists.find((a) => a.artist === name)) {
      const artistData = allArtists.find((a) => a.artist === name);
      if (artistData && selectedArtists.length < 3) {
        setSelectedArtists([...selectedArtists, artistData]);
      }
    }
  };

  const handleClear = () => setSelectedArtists([]);

  return (
    <div
      style={{
        backgroundColor: "#121212",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "'Rubik', sans-serif",
        color: "#EAEAEA",
      }}
    >
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Rubik&display=swap" rel="stylesheet" />
        <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet" />
      </Helmet>

      {/* NAVBAR */}
      <header
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 2fr 1fr",
          alignItems: "center",
          padding: "20px 20px",
          borderBottom: "2px solid #1E1E1E",
        }}
      >
        <h1 style={{ fontSize: "45px", fontWeight: "bold", color: "#04CD46", textTransform: "lowercase", paddingLeft: "20px" }}>
          critica
        </h1>
        <nav style={{ display: "flex", justifyContent: "center", gap: "18px" }}>
          <Link to="/" style={{ textDecoration: "none", color: "white", fontWeight: "bold" }}>HOME</Link>
          <Link to="/charts/rap" style={{ textDecoration: "none", color: "white", fontWeight: "bold" }}>CHARTS</Link>
          <Link to="/compare" style={{ textDecoration: "none", color: "white", fontWeight: "bold" }}>COMPARE</Link>
        </nav>
      </header>

      {/* DROPDOWN + BUTTON */}
      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <h2 style={{ fontSize: "28px", marginBottom: "10px" }}>Compare Up to 3 Artists</h2>

        <select
          onChange={handleSelect}
          defaultValue=""
          style={{
            backgroundColor: "#1E1E1E",
            color: "#EAEAEA",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "8px 12px",
            fontSize: "16px",
            fontFamily: "'Rubik', sans-serif",
            outline: "none",
          }}
        >
          <option value="">Select an artist...</option>
          {allArtists.map((a, i) => (
            <option key={i} value={a.artist}>
              {a.artist}
            </option>
          ))}
        </select>

        <button
          onClick={handleClear}
          style={{
            marginLeft: "15px",
            padding: "8px 12px",
            borderRadius: "8px",
            backgroundColor: "#04CD46",
            color: "#121212",
            border: "none",
            fontWeight: "bold",
            cursor: "pointer",
            fontFamily: "'Rubik', sans-serif",
          }}
        >
          Clear
        </button>
      </div>

      {/* CHARTS */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "25px",
          marginTop: "40px",
          maxWidth: "1200px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {selectedArtists.map((artist, index) => (
          <div
            key={index}
            style={{
              backgroundColor: "#1E1E1E",
              padding: "20px",
              borderRadius: "10px",
              textAlign: "center",
              maxWidth: "350px",
              width: "100%",
            }}
          >
            <h3 style={{ fontSize: "20px", fontFamily: "'Rubik', sans-serif" }}>{artist.artist}</h3>
            <div style={{ width: "100%", height: "200px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={artist.albums}
                  margin={{ top: 10, right: 20, bottom: 40, left: 20 }}
                  onMouseMove={(e) => {
                    const point = e?.activePayload?.[0]?.payload;
                    if (point) {
                      setHoveredAlbums((prev) => ({ ...prev, [artist.artist]: point }));
                    }
                  }}
                  onMouseLeave={() => setHoveredAlbums((prev) => ({ ...prev, [artist.artist]: null }))}
                >
                  <XAxis
                    dataKey="release_year"
                    stroke="#EAEAEA"
                    tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
                    interval={0}
                    tickFormatter={(_, i) => {
                      const years = artist.albums.map((a) => a.release_year);
                      const currentYear = artist.albums[i].release_year;
                      const firstIndex = years.indexOf(Math.min(...years));
                      const lastIndex = years.lastIndexOf(Math.max(...years));
                      return i === firstIndex || i === lastIndex ? currentYear : "";
                    }}
                  />
                  <YAxis
                    domain={[0, 10]}
                    stroke="#EAEAEA"
                    tick={{ fontSize: 12, fontFamily: "Roboto, sans-serif" }}
                    tickFormatter={(val) => (val === 0 || val === 10 ? val : "")}
                  />
                  <Line type="monotone" dataKey="pitchfork" stroke="#057EFC" strokeWidth={3.45} dot={false} />
                  <Line type="monotone" dataKey="metacritic" stroke="#04CD46" strokeWidth={3.45} dot={false} />
                  <Line type="monotone" dataKey="fantano" stroke="#FCDD0C" strokeWidth={3.45} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {hoveredAlbums[artist.artist] && (
              <div style={{ fontFamily: "Roboto, sans-serif", marginTop: "10px", fontSize: "14px" }}>
              <p>
                <strong>{hoveredAlbums[artist.artist].Album || "Unknown Album"}</strong> ({hoveredAlbums[artist.artist].release_year})
              </p>
              <p style={{ color: "#057EFC" }}>Pitchfork: {hoveredAlbums[artist.artist].pitchfork}</p>
              <p style={{ color: "#04CD46" }}>Metacritic: {hoveredAlbums[artist.artist].metacritic}</p>
              <p style={{ color: "#FCDD0C" }}>Fantano: {hoveredAlbums[artist.artist].fantano}</p>
            </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Compare;
